/**
 * NotificationBadge Component
 *
 * Bell icon with unread count badge.
 * Opens the NotificationCenter when clicked.
 */

import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNotificationContext } from '@/context/NotificationContext';
import { cn } from '@/lib/utils';

export function NotificationBadge() {
  const { unreadCount, countByPriority, systemNotificationCount, isOpen, setIsOpen } = useNotificationContext();

  const hasUrgent = countByPriority.urgent > 0;
  const hasActionRequired = countByPriority.action_required > 0;
  const totalCount = unreadCount + systemNotificationCount;

  // Determine badge color based on highest priority
  const getBadgeColor = () => {
    if (hasUrgent) return 'bg-red-500';
    if (hasActionRequired) return 'bg-amber-500';
    return 'bg-primary';
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        'h-8 w-8 relative border border-input',
        isOpen && 'bg-accent'
      )}
      onClick={() => setIsOpen(!isOpen)}
      aria-label={`Notifications${totalCount > 0 ? ` (${totalCount} unread)` : ''}`}
    >
      <Bell className={cn(
        'h-4 w-4 transition-colors',
        hasUrgent && 'text-red-500',
      )} />

      {/* Unread indicator dot */}
      {totalCount > 0 && (
        <span
          className={cn(
            'absolute top-0 right-0 block h-2 w-2 rounded-full',
            'transform translate-x-1/2 -translate-y-1/2',
            'ring-2 ring-background',
            getBadgeColor()
          )}
        />
      )}

      {/* Pulse animation for urgent */}
      {hasUrgent && (
        <span
          className={cn(
            'absolute top-0 right-0 block h-2 w-2 rounded-full',
            'transform translate-x-1/2 -translate-y-1/2',
            'animate-ping bg-red-500 opacity-75'
          )}
        />
      )}

      <span className="sr-only">View notifications</span>
    </Button>
  );
}
